import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, collection, addDoc, query, where, getDocs, onSnapshot, serverTimestamp, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { VIDEO_BASE_URL } from '../../config/api';
import {
  BookOpen, Loader2, ArrowRight, CheckCircle2, GraduationCap,
  Users, AlertCircle, Play, Star, Clock, Share2, FileText, ChevronUp, ChevronDown,
  Award, Layers, Video, Infinity, Lock, Copy, ShieldCheck, Edit2, Heart
} from 'lucide-react';
import CourseTrailerModal from '../../components/dashboard/CourseTrailerModal';
import { notifyAdmins } from '../../utils/notificationService';
import ReviewDisplay from '../../components/reviews/ReviewDisplay';
import ReviewSubmissionModal from '../../components/reviews/ReviewSubmissionModal';
import CourseReviewBadge from '../../components/reviews/CourseReviewBadge';
import InstructorReviewBadge from '../../components/reviews/InstructorReviewBadge';
import PaymentModal from '../../components/PaymentModal';

const LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };

const formatDuration = (min, sec) => {
  const m = parseInt(min) || 0;
  const s = parseInt(sec) || 0;
  if (m === 0 && s === 0) return '';
  return m > 0 ? `${m}:${String(s).padStart(2, '0')} د` : `${s} ث`;
};

const StatCard = ({ icon: Icon, label, value, colorClass, spanTwoMobile }) => (
  <div className={`bg-white dark:bg-zinc-900/40 border border-slate-100 dark:border-white/5 rounded-2xl p-4 sm:p-5 flex items-center justify-between shadow-sm group hover:shadow-md hover:border-slate-200 dark:hover:border-white/10 transition-all duration-500 ${spanTwoMobile ? 'col-span-2 lg:col-span-1' : ''}`}>
    <div className="flex flex-col gap-0.5">
      <span className="text-md sm:text-sm font-semibold text-slate-400 dark:text-zinc-500 uppercase tracking-tight">{label}</span>
      <span className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{value}</span>
    </div>
    <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-[1.25rem] flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:scale-110 shadow-sm ${colorClass}`}>
      <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
    </div>
  </div>
);

const CollapsibleCard = ({ icon: Icon, title, children, isOpen, onToggle, colorClass }) => (
  <div className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-3xl overflow-hidden shadow-sm transition-all duration-500 w-full mb-6">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-6 sm:p-8 hover:bg-slate-50/50 dark:hover:bg-white/5 transition-colors text-right"
    >
      <div className="flex items-center gap-4 sm:gap-6">
        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${colorClass}`}>
          <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
        </div>
        <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">{title}</h3>
      </div>
      <div className={`transition-transform duration-500 ease-out p-2 rounded-full bg-slate-100 dark:bg-zinc-800 ${isOpen ? '' : 'rotate-180'}`}>
        <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 text-slate-500 dark:text-zinc-400" />
      </div>
    </button>

    <div className={`transition-all duration-700 ease-in-out overflow-hidden ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
      <div className="px-6 sm:px-10 pb-10 pt-2">
        {children}
      </div>
    </div>
  </div>
);

const InstructorCard = ({ instructor, slugOrId }) => {
  if (!instructor) return null;
  return (
    <div className="group relative bg-white/40 dark:bg-zinc-900/40 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-2xl p-6 sm:p-8 overflow-hidden shadow-sm transition-all duration-700 hover:shadow-primary-500/5">
      <div className="relative z-10 flex flex-col items-center gap-6">
        {/* Profile Image Section */}
        <div className="relative shrink-0">
          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-3xl overflow-hidden shadow-2xl relative z-10 border-4 border-white dark:border-zinc-800 bg-slate-100 dark:bg-zinc-800 transition-transform duration-500 group-hover:scale-105">
            {instructor.photoURL ? (
              <img src={instructor.photoURL} alt={instructor.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            ) : (
              <Users className="w-14 h-14 text-slate-300 dark:text-zinc-600" />
            )}
          </div>
          {/* Floating Badge */}
          <div className="absolute -bottom-2 -right-2 z-20 bg-amber-500 rounded-2xl p-2 shadow-xl shadow-amber-500/30 transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
            <Award className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 text-center w-full">
          <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">{instructor.name}</h3>
          <div className="inline-flex px-4 py-1.5 rounded-full bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/20 text-primary-600 dark:text-primary-400 text-sm font-black mb-4">
            {instructor.instructorData?.specialization || 'مدرب معتمد'}
          </div>

          <div className="mb-4 flex justify-center">
            <InstructorReviewBadge instructorId={instructor.id} />
          </div>

          <p className="text-base text-slate-600 dark:text-zinc-400 leading-relaxed font-medium mb-8 line-clamp-4">
            {instructor.instructorData?.bio || 'شغف بالتصميم، التزام بالإبداع، وخبرة ممتدة في صناعة المحتوى البصري الاحترافي.'}
          </p>

          <Link
            to={`/instructor/${instructor.id}`}
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl font-black bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-primary-600 dark:hover:bg-primary-500 hover:text-white dark:hover:text-white transition-all duration-300 shadow-xl shadow-slate-900/10 dark:shadow-white/5 group/btn"
          >
            <span>عرض ملف الشخصي للمدرب</span>
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover/btn:-translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function CourseDetail() {
  const { slugOrId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isProfileComplete, userData } = useAuth();
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [activeBottomTab, setActiveBottomTab] = useState('curriculum');
  const [isDownloadingPath, setIsDownloadingPath] = useState(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(true);
  const [isLearnOpen, setIsLearnOpen] = useState(true);
  const [isReqsOpen, setIsReqsOpen] = useState(true);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [course, setCourse] = useState(null);
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [openUnits, setOpenUnits] = useState({});
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [courseId, setCourseId] = useState(null);

  // Listen for edit events from cards
  useEffect(() => {
    const handleOpenEdit = (e) => {
      if (e.detail.targetId === (course?.id || courseId)) {
        setIsReviewOpen(true);
      }
    };
    window.addEventListener('OPEN_REVIEW_EDIT', handleOpenEdit);
    return () => window.removeEventListener('OPEN_REVIEW_EDIT', handleOpenEdit);
  }, [course, courseId]);

  const handleShareCourse = async () => {
    const shareData = {
      title: course?.title || 'دورة في لُباب',
      text: `اطلع على دورة "${course?.title}" في منصة لُباب`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 2500);
      }
    } catch {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2500);
    }
  };

  const handleSecureDownload = async (fileUrl) => {
    if (!currentUser) return alert('الرجاء تسجيل الدخول أولاً.');
    setIsDownloadingPath(fileUrl);
    try {
      const token = await currentUser.getIdToken();
      window.location.href = `${VIDEO_BASE_URL}/api/course/download-file?url=${encodeURIComponent(fileUrl)}&token=${encodeURIComponent(token)}`;
    } catch (err) {
      console.error('Download auth error:', err);
      alert('تعذر توثيق الجلسة للتحميل.');
    } finally {
      setTimeout(() => setIsDownloadingPath(null), 1500);
    }
  };

  const isFavorited = currentUser ? course?.favorites?.includes(currentUser.uid) : false;

  const handleToggleFavorite = async () => {
    if (!currentUser) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      navigate('/login');
      return;
    }
    if (!courseId) return;

    try {
      const courseRef = doc(db, 'courses', courseId);
      if (isFavorited) {
        await updateDoc(courseRef, { favorites: arrayRemove(currentUser.uid) });
      } else {
        await updateDoc(courseRef, { favorites: arrayUnion(currentUser.uid) });
      }
    } catch (e) {
      console.error(e);
      alert('حدث خطأ. حاول مرة أخرى.');
    }
  };



  const toggleUnit = (idx) => setOpenUnits(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Fetch course
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        let snap;
        const q = query(
          collection(db, 'courses'),
          where('slug', '==', slugOrId),
          where('status', '==', 'published')
        );
        const qSnap = await getDocs(q);

        if (!qSnap.empty) {
          snap = qSnap.docs[0];
        } else {
          snap = await getDoc(doc(db, 'courses', slugOrId));
        }

        if (!snap?.exists() || snap.data().status !== 'published') {
          setNotFound(true);
        } else {
          const fetchedCourseId = snap.id;
          const data = { id: fetchedCourseId, ...snap.data() };
          setCourse(data);
          setCourseId(fetchedCourseId);

          if (data.instructorId) {
            const instSnap = await getDoc(doc(db, 'users', data.instructorId));
            if (instSnap.exists()) {
              setInstructor({ id: instSnap.id, ...instSnap.data() });
            }
          }

          // open first unit by default
          if (data.curriculum?.length) setOpenUnits({ 0: true });
        }
      } catch (error) {
        console.error("Error fetching course:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [slugOrId]);

  // Check enrollment & registration status
  useEffect(() => {
    if (!currentUser || !courseId) return;
    let unsubReg;
    const check = async () => {
      const q = query(collection(db, 'enrollments'), where('userId', '==', currentUser.uid), where('courseId', '==', courseId));
      const snap = await getDocs(q);
      setIsEnrolled(!snap.empty);
      const regQ = query(collection(db, 'course_registrations'), where('studentId', '==', currentUser.uid), where('courseId', '==', courseId));
      unsubReg = onSnapshot(regQ, (regSnap) => {
        if (!regSnap.empty) setRegistrationStatus(regSnap.docs[0].data().status || 'interested');
        else setRegistrationStatus(null);
      });
      // Fetch completion progress if already enrolled
      if (!snap.empty) {
        const enrollmentData = snap.docs[0].data();
        if (enrollmentData.completedLessons) {
          setCompletedLessons(new Set(enrollmentData.completedLessons));
        }
        // Check if already reviewed
        const revRef = doc(db, 'reviews', `${currentUser.uid}_${courseId}`);
        getDoc(revRef).then(s => { if (s.exists()) setHasExistingReview(true); });
      }
    };
    check();
    return () => { if (unsubReg) unsubReg(); };
  }, [currentUser, courseId]);

  const handleEnroll = async () => {
    if (!currentUser) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      navigate('/signup');
      return;
    }
    if (registrationStatus || isEnrolled) return;
    setIsPaymentModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500">
        <div className="relative">
          <div className="absolute inset-0 bg-primary-500/20 rounded-full blur-xl animate-pulse"></div>
          <Loader2 className="w-12 h-12 animate-spin text-primary-500 relative z-10" />
        </div>
        <p className="font-bold mt-6 text-lg tracking-wide">جاري إعداد تجربة مبهرة...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500">
        <AlertCircle className="w-20 h-20 text-slate-300 dark:text-zinc-800 mb-6 drop-shadow-lg" />
        <h2 className="text-3xl font-extrabold mb-3 text-slate-800 dark:text-white">لم نتمكن من العثور على الدورة</h2>
        <p className="text-lg mb-8 text-slate-500 dark:text-zinc-400 max-w-md text-center">يبدو أن الدورة التي تبحث عنها غير متاحة حالياً أو تم تغيير رابطها.</p>
        <Link to="/courses" className="flex items-center gap-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-4 rounded-2xl font-bold hover:shadow-lg hover:shadow-primary-500/30 transition-all hover:scale-105">
          <ArrowRight className="w-5 h-5" /> تصفح الدورات المتوفرة
        </Link>
      </div>
    );
  }

  const curriculum = course.curriculum || [];
  const whatYouLearn = course.whatYouLearn || [];
  const requirements = course.requirements || [];
  const isOwnCourse = currentUser?.uid === course?.instructorId;

  // Course Action Card Component to be used in two places (Mobile flow vs Desktop sidebar)
  const ActionCard = (
    <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl rounded-lg border border-slate-200/50 dark:border-white/10 shadow-lg group">
      <div className="p-4 sm:p-6">
        {/* Thumbnail */}
        <div className="relative rounded-lg overflow-hidden aspect-[4/3] bg-slate-100 dark:bg-black group shadow-inner mb-6">
          {course.thumbnail ? (
            <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110 opacity-90 group-hover:opacity-100" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-900">
              <Play className="w-16 h-16 text-slate-300 dark:text-zinc-800" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-500 rounded-[1.5rem] ${course.courseVideoId ? 'bg-black/10 hover:bg-black/30 cursor-pointer' : 'pointer-events-none'}`}
            onClick={() => course.courseVideoId && setIsTrailerOpen(true)}
          >
            {course.courseVideoId && (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-white/95 backdrop-blur-md flex items-center justify-center shadow-[0_0_30px_rgba(230,0,35,0.4)] hover:scale-110 hover:shadow-[0_0_40px_rgba(230,0,35,0.7)] transition-all">
                  <Play className="w-8 h-8 ml-1 text-primary-600" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Price Header */}
        <div className="flex items-center justify-between mb-6">
          <button className="w-12 h-12 rounded-full bg-slate-50 dark:bg-white/5 flex items-center justify-center text-slate-500 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-primary-500 transition-colors border border-slate-100 dark:border-white/5 shadow-sm">
            <Share2 className="w-5 h-5" />
          </button>
          <div className="text-left" dir="ltr">
            {course.price > 0 ? (
              <div className="flex items-start">
                <span className="text-2xl font-black text-slate-400 dark:text-zinc-500 mt-1 mr-2">$</span>
                <span className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>{Number(course.price).toFixed(2)}</span>
              </div>
            ) : (
              <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-l from-emerald-400 to-emerald-600 pb-1">مجاني</span>
            )}
          </div>
        </div>

        {/* Course meta stats */}
        <div className="space-y-3 sm:space-y-4 border-t border-slate-100 dark:border-white/5 pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-slate-600 dark:text-zinc-400">
              <Layers className="w-5 h-5 text-primary-500 shrink-0" />
              <span className="text-md font-bold text-slate-500 dark:text-zinc-400">عدد الوحدات</span>
            </div>
            <span className="text-md font-black text-slate-900 dark:text-white">{course.totalUnits || curriculum.length || 0} وحدة</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-slate-600 dark:text-zinc-400">
              <Clock className="w-5 h-5 text-primary-500 shrink-0" />
              <span className="text-md font-bold text-slate-500 dark:text-zinc-400">عدد الدروس</span>
            </div>
            <span className="text-md font-black text-slate-900 dark:text-white">{course.totalLessons || 0} درس</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-slate-600 dark:text-zinc-400">
              <Video className="w-5 h-5 text-primary-500 shrink-0" />
              <span className="text-md font-bold text-slate-500 dark:text-zinc-400">مدة الدورة</span>
            </div>
            <span className="text-md font-black text-slate-900 dark:text-white">{course.durationLabel || '00:00'}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-slate-600 dark:text-zinc-400">
              <Award className="w-5 h-5 text-primary-500 shrink-0" />
              <span className="text-md font-bold text-slate-500 dark:text-zinc-400">شهادة معتمدة</span>
            </div>
            <span className="text-md font-black text-slate-900 dark:text-white">{course.hasCertificate ? 'نعم' : 'لا'}</span>
          </div>

          <div className="pt-6">
            {isOwnCourse ? (
              <div className="flex items-center justify-center gap-3 bg-amber-50 dark:bg-amber-500/10 rounded-2xl p-5 border border-amber-200 dark:border-amber-500/20 shadow-inner">
                <ShieldCheck className="w-7 h-7 text-amber-500 drop-shadow-sm shrink-0" />
                <p className="font-black text-amber-700 dark:text-amber-400 text-lg">أنت مدرب هذه الدورة</p>
              </div>
            ) : isEnrolled || registrationStatus === 'customer' ? (
              <div className="flex items-center justify-center gap-3 bg-primary-50 dark:bg-primary-500/10 rounded-2xl p-5 border border-primary-100 dark:border-primary-500/20 shadow-inner">
                <CheckCircle2 className="w-7 h-7 text-primary-500 drop-shadow-sm shrink-0" />
                <p className="font-black text-primary-700 dark:text-primary-400 text-lg">أنت تمتلك هذه الدورة</p>
              </div>
            ) : (registrationStatus === 'interested' || registrationStatus === 'contacted' || registrationStatus === 'pending') ? (
              <div className="flex items-center justify-center gap-3 bg-slate-50 dark:bg-white/5 rounded-2xl p-5 border border-slate-200 dark:border-white/10 shadow-inner">
                <Clock className="w-7 h-7 text-slate-400 dark:text-zinc-500 shrink-0" />
                <p className="font-black text-slate-800 dark:text-white text-lg">الطلب قيد التوثيق</p>
              </div>
            ) : !currentUser ? (
              <div className="flex flex-col gap-3">
                <Link to="/signup" className="block text-center w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-[1.25rem] font-black text-lg transition-all hover:bg-slate-800 dark:hover:bg-zinc-200 shadow-xl shadow-black/10 hover:-translate-y-0.5">
                  إنشاء حساب مجاني للاشتراك
                </Link>
                <Link to="/login" className="block text-center w-full bg-white dark:bg-zinc-900 text-slate-900 dark:text-white border-2 border-slate-200 dark:border-white/10 py-4 rounded-[1.25rem] font-bold text-md transition-all hover:bg-slate-50 dark:hover:bg-zinc-800">
                  لديك حساب؟ تسجيل دخول
                </Link>
              </div>
            ) : !isProfileComplete ? (
              <Link to="/dashboard/settings" className="block text-center w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-5 rounded-[1.25rem] font-black text-lg transition-all hover:bg-slate-800 dark:hover:bg-zinc-200 shadow-xl shadow-black/10">
                أكمل الملف الشخصي للاشتراك
              </Link>
            ) : (
              <div className="relative group">
                <div className="absolute inset-0 bg-primary-600 rounded-[1.25rem] blur-[24px] opacity-40 group-hover:opacity-70 transition-opacity duration-700 pointer-events-none"></div>
                <button onClick={handleEnroll} disabled={enrolling}
                  className="relative w-full flex items-center justify-center gap-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-5 rounded-[1.25rem] font-black text-xl transition-all disabled:opacity-50 z-10 hover:-translate-y-1 shadow-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out"></div>
                  {enrolling ? <Loader2 className="w-6 h-6 animate-spin" /> : null}
                  <span className="relative z-10">{enrolling ? 'جاري الإرسال...' : 'التسجيل في الدورة'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 max-w-6xl mx-auto pb-24 px-4 sm:px-6 relative ">
      {/* SEO: Dynamic Meta Tags, Open Graph & JSON-LD */}
      <Helmet>
        <title>{`${course.title} — لُباب`}</title>
        <meta name="description" content={(course.shortDesc || course.description || '').slice(0, 160)} />
        <link rel="canonical" href={`https://lobabschool.com/courses/${course.slug || slugOrId}`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${course.title} — لُباب`} />
        <meta property="og:description" content={(course.shortDesc || course.description || '').slice(0, 200)} />
        <meta property="og:url" content={`https://lobabschool.com/courses/${course.slug || slugOrId}`} />
        {course.thumbnail && <meta property="og:image" content={course.thumbnail} />}
        <meta property="og:site_name" content="Lobab School | لُباب" />
        <meta property="og:locale" content="ar_SA" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${course.title} — لُباب`} />
        <meta name="twitter:description" content={(course.shortDesc || course.description || '').slice(0, 200)} />
        {course.thumbnail && <meta name="twitter:image" content={course.thumbnail} />}

        {/* Schema.org Course JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: course.title,
            description: course.shortDesc || course.description || '',
            url: `https://lobabschool.com/courses/${course.slug || slugOrId}`,
            image: course.thumbnail || undefined,
            provider: {
              '@type': 'Organization',
              name: 'Lobab School | لُباب',
              url: 'https://lobabschool.com',
              sameAs: 'https://lobabschool.com',
            },
            ...(instructor && {
              instructor: {
                '@type': 'Person',
                name: instructor.name,
                ...(instructor.photoURL && { image: instructor.photoURL }),
                ...(instructor.instructorData?.specialization && {
                  jobTitle: instructor.instructorData.specialization,
                }),
              },
            }),
            ...(course.level && {
              educationalLevel: LEVELS[course.level] || course.level,
            }),
            ...(course.totalLessons && {
              numberOfLessons: course.totalLessons,
            }),
            ...(course.durationLabel && {
              timeRequired: course.durationLabel,
            }),
            ...(course.price !== undefined && {
              offers: {
                '@type': 'Offer',
                price: course.price,
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
                url: `https://lobabschool.com/courses/${course.slug || slugOrId}`,
              },
            }),
            inLanguage: 'ar',
          })}
        </script>
      </Helmet>

      {/* Ambient Grid Background */}
      <div className="fixed inset-0 pointer-events-none bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.02] dark:opacity-5 mix-blend-overlay -z-10"></div>

      {/* Share Toast */}
      {showShareToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-2xl font-bold text-sm">
            <CheckCircle2 className="w-5 h-5" /> تم نسخ رابط الدورة!
          </div>
        </div>
      )}

      {/* Top Bar Navigation */}
      <div className="mb-8 flex items-center justify-between">
        <Link to="/courses"
          className="inline-flex items-center text-xl gap-3 text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium transition-colors group">
          <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          سوق الدورات
        </Link>
        <div className="flex gap-2">
          <button
            onClick={handleToggleFavorite}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all font-bold text-sm shadow-sm ${
              isFavorited
                ? 'bg-rose-50 border-rose-200 text-rose-600 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400'
                : 'bg-white dark:bg-zinc-900 border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 hover:text-rose-500 dark:hover:text-rose-400 hover:border-rose-200 dark:hover:border-rose-500/20'
            }`}
          >
            <Heart className={`w-4 h-4 transition-all duration-300 ${isFavorited ? 'fill-current scale-110' : ''}`} />
            <span className="hidden sm:inline">{isFavorited ? 'مضافة للمفضلة' : 'إضافة للمفضلة'}</span>
          </button>
          <button
            onClick={handleShareCourse}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-200 dark:hover:border-amber-500/20 transition-all font-bold text-sm shadow-sm"
          >
            <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">مشاركة رابط الدورة</span>
          </button>
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start ">

        {/* RIGHT COLUMN (Arabic Flow) - Content */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-12">

          {/* Header / Title Area */}
          <div className="space-y-6 relative z-10">
            <div className="absolute top-0 right-0 md:right-10 w-64 h-64 md:w-[400px] md:h-[400px] bg-primary-500/10 rounded-full blur-[80px] md:blur-[100px] -translate-y-1/2 pointer-events-none -z-10"></div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-1.5 bg-primary-50 dark:bg-primary-500/10 border border-primary-500/20 text-primary-600 dark:text-primary-400 backdrop-blur-md rounded-xl">
                <span className="text-md font-black">{course.category}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-1.5 bg-white/70 dark:bg-zinc-900/50 backdrop-blur-md border border-slate-200 dark:border-white/5 shadow-sm rounded-xl">
                <span className="w-2 h-2 rounded-full bg-primary-500 shadow-[0_0_8px_rgba(230,0,35,0.8)]"></span>
                <span className="text-md font-black text-slate-700 dark:text-zinc-300">{LEVELS[course.level] || course.level}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50/80 dark:bg-amber-500/10 backdrop-blur-md border border-amber-200/50 dark:border-amber-500/20 shadow-sm rounded-xl">
                <CourseReviewBadge courseId={course.id} />
              </div>
            </div>
            <div className="space-y-4 md:space-y-5">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black text-slate-900 dark:text-white leading-[1.25] md:leading-[1.1] tracking-tight drop-shadow-sm">
                {course.title}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-500 dark:text-zinc-400 leading-relaxed max-w-3xl">
                {course.shortDesc || course.description}
              </p>
            </div>
          </div>


          {/* Stats Matrix Pills */}
          {/* Stats Matrix Pills */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 relative z-10 w-full mb-16">
            <StatCard
              icon={Users}
              label="المتدربين"
              value={course.studentsCount || 0}
              colorClass="bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400"
            />
            <StatCard
              icon={BookOpen}
              label="المحتوى"
              value={course.totalLessons || curriculum.reduce((acc, unit) => acc + (unit.lessons?.length || 0), 0) || 0}
              colorClass="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
            />
            <StatCard
              icon={Clock}
              label="المدة"
              value={course.durationLabel || '—'}
              colorClass="bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"
              spanTwoMobile
            />
          </div>

          <div className="lg:hidden relative mt-4">
            <div className="absolute inset-4 bg-gradient-to-b from-primary-500/20 to-purple-500/20 blur-2xl opacity-40 rounded-[2rem] pointer-events-none -z-10"></div>
            {ActionCard}
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 dark:via-white/10 to-transparent my-12 hidden lg:block opacity-50 relative z-10"></div>

          {/* Collapsible Content Sections */}
          <div className="space-y-4 pt-4 lg:pt-0 relative z-10 w-full font-bold">
            {/* About the course */}
            {course.longDesc && (
              <CollapsibleCard
                icon={BookOpen}
                title="عن هذه الدورة"
                isOpen={isAboutOpen}
                onToggle={() => setIsAboutOpen(!isAboutOpen)}
                colorClass="bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400"
              >
                <p className="text-base sm:text-lg text-slate-600 dark:text-zinc-300 font-medium leading-[1.8] whitespace-pre-wrap">
                  {course.longDesc}
                </p>
              </CollapsibleCard>
            )}

            {/* What you'll learn */}
            <CollapsibleCard
              icon={CheckCircle2}
              title="ماذا ستتعلم؟"
              isOpen={isLearnOpen}
              onToggle={() => setIsLearnOpen(!isLearnOpen)}
              colorClass="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
            >
              {whatYouLearn.length > 0 ? (
                <ul className="space-y-5">
                  {whatYouLearn.map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="mt-1.5 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      </div>
                      <span className="text-base sm:text-lg text-slate-600 dark:text-zinc-300 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base sm:text-lg text-slate-500 dark:text-zinc-500 font-medium leading-relaxed italic">
                  {course.description || 'محاور الدورة وخبرات التصميم سيتم تفصيلها هنا.'}
                </p>
              )}
            </CollapsibleCard>

            {/* Requirements */}
            <CollapsibleCard
              icon={AlertCircle}
              title="متطلبات الدورة"
              isOpen={isReqsOpen}
              onToggle={() => setIsReqsOpen(!isReqsOpen)}
              colorClass="bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"
            >
              {requirements.length > 0 ? (
                <ul className="space-y-4">
                  {requirements.map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="mt-1.5 w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      </div>
                      <span className="text-base sm:text-lg text-slate-600 dark:text-zinc-300 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base sm:text-lg text-slate-500 dark:text-zinc-400 font-medium leading-relaxed italic">
                  لا توجد متطلبات خاصة، هذه الدورة مصممة لتبدأ معك من الصفر.
                </p>
              )}
            </CollapsibleCard>
          </div>

          {/* Bottom Tabs */}
          <div className="pt-8">
            <div className="flex border-b border-slate-200 dark:border-zinc-800 mb-8 gap-8">
              {curriculum.length > 0 && (
                <button
                  onClick={() => setActiveBottomTab('curriculum')}
                  className={`pb-4 text-xl font-bold transition-colors border-b-2 ${activeBottomTab === 'curriculum' ? 'border-primary-600 text-primary-600 dark:text-primary-500' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-500 dark:hover:text-zinc-300'}`}
                >
                  محتوى الدورة
                </button>
              )}
              {course.attachments && course.attachments.length > 0 && (
                <button
                  onClick={() => setActiveBottomTab('attachments')}
                  className={`pb-4 text-xl font-bold transition-colors border-b-2 flex items-center gap-2 ${activeBottomTab === 'attachments' ? 'border-primary-600 text-primary-600 dark:text-primary-500' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-500 dark:hover:text-zinc-300'}`}
                >
                  ملحقات الدورة
                  <span className="bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 text-md py-0.5 px-2 rounded-full font-bold">{course.attachments.length}</span>
                </button>
              )}
            </div>

            {/* Content: Curriculum */}
            {activeBottomTab === 'curriculum' && curriculum.length > 0 && (
              <div className="relative space-y-4 animate-in fade-in duration-500">
                {/* Timeline track */}
                <div className="absolute right-3.5 top-8 bottom-8 w-px bg-slate-400 dark:bg-white/10 hidden md:block"></div>

                {curriculum.map((unit, ui) => {
                  const isOpen = openUnits[ui];
                  const lessonsCount = unit.lessons?.length || 0;
                  return (
                    <div key={ui} className="relative flex items-start gap-4 md:gap-10">
                      <div className="mt-6 relative z-10 w-8 h-8 rounded-full bg-slate-80 dark:bg-[#0f0f0f] border border-slate-200 dark:border-white/10 hidden md:flex items-center justify-center shadow-lg">
                        <div className="w-3 h-3 rounded-full bg-primary-600 shadow-[0_0_10px_rgba(230,0,35,0.7)]"></div>
                      </div>
                      <div className="flex-1 bg-slate-50 dark:bg-[#0f0f0f] border border-slate-200 dark:border-white/5 rounded-2xl overflow-hidden shadow-[0_10px_30px_-15px_rgba(0,0,0,0.05)] dark:shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)]">
                        <button type="button" onClick={() => toggleUnit(ui)}
                          className="w-full px-4 sm:px-6 md:px-8 py-5 sm:py-6 flex items-center justify-between cursor-pointer group text-right">
                          <div>
                            <h4 className="text-lg font-black text-slate-900 dark:text-white group-hover:text-primary-600 transition-colors">
                              الوحدة {ui + 1}: {unit.title || 'بدون عنوان'}
                            </h4>
                            <p className="text-md text-red-600 dark:text-zinc-500 mt-1 font-bold">{lessonsCount} دروس</p>
                          </div>
                          {isOpen ? <ChevronUp className="w-6 h-6 text-slate-400 dark:text-zinc-500" /> : <ChevronDown className="w-6 h-6 text-slate-400 dark:text-zinc-500" />}
                        </button>
                        {isOpen && unit.lessons?.map((lesson, li) => {
                          const isLocked = lesson.accessLevel === 'subscriber' && !isEnrolled && registrationStatus !== 'customer';
                          const type = lesson.type || 'video';
                          let theme = { bg: 'bg-slate-50 dark:bg-white/5', iconColor: 'text-slate-500', hover: 'hover:bg-slate-100 dark:hover:bg-white/10', Icon: Play };

                          if (isLocked) {
                            theme = { bg: 'bg-slate-50 dark:bg-zinc-800/50', iconColor: 'text-slate-400 dark:text-zinc-500', hover: '', Icon: Lock };
                          } else if (type === 'video') {
                            theme = { bg: 'bg-blue-50 dark:bg-blue-500/10', iconColor: 'text-blue-500 dark:text-blue-400', hover: 'hover:bg-blue-50/50 dark:hover:bg-blue-500/20', Icon: Play };
                          } else if (type === 'text') {
                            theme = { bg: 'bg-amber-50 dark:bg-amber-500/10', iconColor: 'text-amber-500 dark:text-amber-400', hover: 'hover:bg-amber-50/50 dark:hover:bg-amber-500/20', Icon: FileText };
                          } else if (type === 'quiz') {
                            theme = { bg: 'bg-purple-50 dark:bg-purple-500/10', iconColor: 'text-purple-500 dark:text-purple-400', hover: 'hover:bg-purple-50/50 dark:hover:bg-purple-500/20', Icon: Award };
                          }
                          const { Icon } = theme;
                          return (
                            <Link key={li} to={`/courses/${slugOrId}/watch?lesson=u${ui}-l${li}`}
                              className={`px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-t border-slate-100 dark:border-white/5 flex items-center justify-between gap-6 sm:gap-4 transition-colors cursor-pointer bg-white dark:bg-[#141414] ${theme.hover}`}>
                              <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm border border-slate-100 dark:border-white/5 transition-colors ${theme.bg}`}>
                                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${theme.iconColor}`} />
                                </div>
                                <div className="flex flex-col min-w-0 pr-1">
                                  <p className={`text-md sm:text-[1.05rem] font-bold truncate ${isLocked ? 'text-slate-400 dark:text-zinc-500' : 'text-slate-700 dark:text-zinc-200'} flex items-center break-words whitespace-normal leading-tight ml-2`}>
                                    <span className="opacity-60 text-sm sm:text-md ml-1.5 sm:ml-2 font-black shrink-0">{li + 1}.</span>
                                    <span className="line-clamp-2">{lesson.title || 'بدون عنوان'}</span>
                                    {completedLessons.has(`u${ui}-l${li}`) && (
                                      <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 shrink-0 mr-1.5" />
                                    )}
                                  </p>
                                  {lesson.description && (
                                    <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-zinc-500 mt-1 line-clamp-1" title={lesson.description}>
                                      {lesson.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-3 shrink-0">
                                {formatDuration(lesson.durationMin, lesson.durationSec) && (
                                  <span className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-slate-500 dark:text-zinc-400 bg-white dark:bg-zinc-800/80 px-2 py-0.5 sm:py-1 rounded-md shrink-0">
                                    <Clock className="w-3 h-3 text-primary-500 hidden sm:block" />
                                    <span dir="ltr" className="font-mono">{formatDuration(lesson.durationMin, lesson.durationSec)}</span>
                                  </span>
                                )}
                                {!isEnrolled && lesson.accessLevel === 'public' && (
                                  <span className="text-[10px] sm:text-xs font-bold text-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded flex items-center justify-center">مجاني</span>
                                )}
                                {!isEnrolled && lesson.accessLevel === 'subscriber' && (
                                  <span className="text-[10px] sm:text-xs font-bold text-amber-500 bg-amber-50 dark:bg-amber-500/10 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded flex items-center justify-center gap-1">
                                    <Lock className="w-3 h-3 hidden sm:block" /> قفل
                                  </span>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Content: Attachments */}
            {activeBottomTab === 'attachments' && course.attachments?.length > 0 && (
              <div className="space-y-8 animate-in fade-in duration-500">
                {course.attachments.map((groupOrAtt, gi) => {
                  const isLegacy = typeof groupOrAtt.fileUrl !== 'undefined' || typeof groupOrAtt.fileName !== 'undefined';
                  const groupName = isLegacy ? 'ملحقات عامة' : groupOrAtt.groupName;
                  const files = isLegacy ? [groupOrAtt] : (groupOrAtt.files || []);

                  if (!isLegacy && files.length === 0) return null;

                  return (
                    <div key={gi} className="space-y-4">
                      {/* Section Header */}
                      {!isLegacy && groupName && (
                        <h3 className="text-xl font-bold text-slate-800 dark:text-zinc-200 border-b border-slate-200 dark:border-zinc-800 pb-2">{groupName}</h3>
                      )}

                      <div className="space-y-4">
                        {files.map((att, i) => (
                          <div key={`${gi}-${i}`} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white dark:bg-[#0f0f0f] border border-slate-200 dark:border-white/5 rounded-2xl shadow-[0_10px_30px_-15px_rgba(0,0,0,0.05)] dark:shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)] group hover:border-primary-500/30 transition-all gap-4">
                            <div className="flex items-center gap-4">
                              {att.fileType === 'link' ? (
                                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
                                  <Link className="w-6 h-6 text-blue-500" />
                                </div>
                              ) : (
                                <div className="w-12 h-12 rounded-2xl bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center shrink-0">
                                  <FileText className="w-6 h-6 text-rose-500" />
                                </div>
                              )}
                              <div>
                                <h4 className="text-lg font-bold text-slate-800 dark:text-zinc-200" dir="ltr">{att.fileName}</h4>
                                <p className="text-md flex items-center gap-2 text-slate-500 dark:text-zinc-500 font-medium mt-1">
                                  <span>{att.fileType === 'link' ? 'رابط خارجي' : 'ملف مرفق'}</span>
                                  {att.fileSize && <span className="text-[11px] font-bold text-primary-500 bg-primary-50 dark:bg-primary-500/10 px-1.5 py-0.5 rounded-md" dir="ltr">{att.fileSize}</span>}
                                </p>
                              </div>
                            </div>
                            <div>
                              {(isEnrolled || registrationStatus === 'customer') ? (
                                att.fileType === 'link' ? (
                                  <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-500 dark:hover:bg-blue-500/20 transition-all w-full sm:w-auto">
                                    زيارة <ArrowRight className="w-4 h-4 rotate-180" />
                                  </a>
                                ) : (
                                  <button onClick={() => handleSecureDownload(att.fileUrl)} disabled={isDownloadingPath === att.fileUrl} className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-md bg-primary-50 text-primary-600 hover:bg-primary-100 dark:bg-primary-500/10 dark:text-primary-500 dark:hover:bg-primary-500/20 transition-all w-full sm:w-auto disabled:opacity-50">
                                    {isDownloadingPath === att.fileUrl ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        تنزيل <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                      </>
                                    )}
                                  </button>
                                )
                              ) : (
                                <div className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-md bg-slate-50 text-slate-400 dark:bg-zinc-800/50 dark:text-zinc-500 cursor-not-allowed group-hover:text-amber-500 group-hover:bg-amber-50 dark:group-hover:bg-amber-500/10 transition-all w-full sm:w-auto" title="التحميل متاح للمشتركين فقط">
                                  مغلق <Lock className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reviews Section */}
            <div className="mt-16 border-t border-slate-200 dark:border-white/10 pt-16">
              {isEnrolled && (
                <div className="mb-12 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 bg-amber-50 dark:bg-amber-500/5 p-6 sm:p-8 rounded-3xl border border-amber-200 dark:border-amber-500/10 shadow-sm">
                  <div className="flex flex-col">
                    <h3 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                      <Star className="w-6 h-6 text-amber-500 fill-amber-500" /> قيم هذه الدورة
                    </h3>
                    <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mt-1">
                      رأيك يهمنا ويساعدنا في تطوير المحتوى وتقديم تجربة أفضل!
                    </p>
                  </div>
                  <button
                    onClick={() => setIsReviewOpen(true)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white shadow-lg shadow-amber-500/20 rounded-xl font-bold transition-all hover:-translate-y-0.5"
                  >
                    {hasExistingReview ? (
                      <>
                        <Edit2 className="w-5 h-5" />
                        تعديل تقييمك
                      </>
                    ) : (
                      <>
                        <Star className="w-5 h-5" />
                        إضافة تقييم
                      </>
                    )}
                  </button>
                </div>
              )}
              <ReviewDisplay targetType="course" targetId={course.id} />
            </div>
          </div>

        </div>

        {/* LEFT COLUMN (Action Flow) - High-End Floating Cards */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="sticky top-32 space-y-8 relative">
            {/* Cinematic Floating Glow behind the card */}
            <div className="absolute inset-4 bg-gradient-to-b from-primary-500/20 to-purple-500/20 blur-3xl opacity-50 rounded-[3rem] pointer-events-none -z-10"></div>

            {/* Action Card (Desktop Only) */}
            <div className="hidden lg:block">
              {ActionCard}
            </div>

            {/* Instructor Card */}
            <InstructorCard instructor={instructor} slugOrId={slugOrId} />
          </div>
        </div>

      </div>

      {isTrailerOpen && course?.courseVideoId && (
        <CourseTrailerModal
          courseVideoId={course.courseVideoId}
          onClose={() => setIsTrailerOpen(false)}
        />
      )}

      {/* Review Modal */}
      <ReviewSubmissionModal
        isOpen={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        user={currentUser}
        targetType="course"
        targetId={course?.id || courseId}
        onSuccess={() => window.location.reload()}
      />
      
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        course={course}
        currentUser={currentUser}
        onSuccess={() => setRegistrationStatus('pending')}
      />
      {/* Floating CTA (Fixed Bottom) globally visible */}
      {!isOwnCourse && !isEnrolled && registrationStatus !== 'customer' && registrationStatus !== 'contacted' && registrationStatus !== 'interested' && (
        <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white/90 dark:bg-zinc-950/90 backdrop-blur-xl border-t border-slate-200 dark:border-white/10 p-4 shadow-[0_-20px_40px_rgba(0,0,0,0.05)] dark:shadow-[0_-20px_50px_rgba(0,0,0,0.5)] animate-in slide-in-from-bottom duration-500 flex items-center justify-between gap-4 pb-safe">
          <div className="flex flex-col">
            <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">الاستثمار</span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black text-slate-900 dark:text-white">${course.newPrice || course.price}</span>
              {(course.oldPrice || course.price > course.newPrice) && (
                <span className="text-md font-bold text-slate-400 line-through">${course.oldPrice || course.price}</span>
              )}
            </div>
          </div>

          <div className="flex-1 max-w-[200px] shrink-0">
            {!currentUser ? (
              <Link to="/signup" className="flex items-center justify-center w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-3 rounded-[1rem] font-black text-[1.05rem] shadow-lg hover:scale-105 transition-all">
                إنشاء حساب
              </Link>
            ) : !isProfileComplete ? (
              <Link to="/dashboard/settings" className="flex items-center justify-center w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-3 rounded-[1rem] font-black text-[1.05rem] shadow-lg hover:scale-105 transition-all">
                أكمل ملفك
              </Link>
            ) : (
              <button onClick={handleEnroll} disabled={enrolling} className="flex items-center justify-center w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white py-3 rounded-[1rem] font-black text-[1.05rem] shadow-lg shadow-primary-500/20 active:scale-95 transition-all outline-none border-none">
                {enrolling ? <Loader2 className="w-5 h-5 animate-spin" /> : 'اقتناء الإصدار'}
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
